import React from 'react'
import { Link } from 'react-router-dom'
import InvoiceGenerator from '../components/InvoiceGenerator'

const OnlineBillingSoftwarePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Simple Online Billing Software for Small Business
            </h1>
            <p className="text-2xl text-blue-100 mb-8">
              No signup. No subscription. No complexity. Just instant, professional billing—completely free.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>100% Free Forever</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>No Login Required</span>
              </div>
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg backdrop-blur">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Instant PDF Generation</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Generator Widget */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Start Billing Online Right Now</h2>
            <p className="text-lg text-gray-600">Create your first invoice in under 60 seconds. No credit card, no account setup.</p>
          </div>
          <InvoiceGenerator />
        </div>

        {/* Why Choose Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Why Choose Simple Online Billing Software?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">🚫 Traditional Billing Software Problems</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">✗</span>
                  <span>Monthly subscription fees ($15-$50/month)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">✗</span>
                  <span>Forced account creation with email verification</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">✗</span>
                  <span>Complex setup requiring hours of configuration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">✗</span>
                  <span>Learning curve for basic invoicing tasks</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">✗</span>
                  <span>Feature bloat you'll never use</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl shadow-lg p-8 border-l-4 border-green-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">✅ InvoiceMeNow Advantages</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>$0/month forever</strong> — completely free</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Zero signup</strong> — start billing instantly</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>No setup</strong> — open page, create invoice, done</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Intuitive interface</strong> — anyone can use it</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Just what you need</strong> — clean, simple billing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Everything You Need to Bill Online Instantly
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: '⚡',
                title: 'Instant Invoice Creation',
                description: 'Generate professional invoices in seconds. No templates to download, no software to install.'
              },
              {
                icon: '📧',
                title: 'Email Delivery',
                description: 'Send invoices directly to clients via email. Professional, fast, and trackable.'
              },
              {
                icon: '💰',
                title: 'Multi-Currency Support',
                description: 'Bill in USD, EUR, GBP, CAD, AUD. Perfect for international freelancers and businesses.'
              },
              {
                icon: '📄',
                title: 'PDF Export',
                description: 'Download professional PDF invoices ready for printing or digital sharing.'
              },
              {
                icon: '💳',
                title: 'Payment Integration',
                description: 'Accept online payments with built-in Stripe integration. Get paid faster.'
              },
              {
                icon: '🎨',
                title: 'AI Logo Generation',
                description: 'Create custom company logos with AI. Professional branding in one click.'
              },
              {
                icon: '🔒',
                title: 'Secure & Private',
                description: 'Your data is encrypted and secure. Optional account creation for history tracking.'
              },
              {
                icon: '📱',
                title: 'Mobile Friendly',
                description: 'Bill clients on the go. Fully responsive design works on any device.'
              },
              {
                icon: '🌍',
                title: 'Tax Compliant',
                description: 'Automatic tax calculations, VAT support, and compliant invoice formatting.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-shadow">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="mb-16 bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Perfect Online Billing Software For:
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Freelancers', desc: 'Quick invoicing between projects' },
              { title: 'Consultants', desc: 'Professional client billing' },
              { title: 'Small Businesses', desc: 'Simple business invoicing' },
              { title: 'Contractors', desc: 'Job-by-job billing' },
              { title: 'Agencies', desc: 'Client project invoices' },
              { title: 'Tradespeople', desc: 'On-site job billing' },
              { title: 'Shopify Sellers', desc: 'eCommerce order invoices' },
              { title: 'Service Providers', desc: 'Service billing made easy' }
            ].map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-sm text-gray-600">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            How We Compare to Traditional Billing Software
          </h2>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Feature</th>
                    <th className="px-6 py-4 text-center">InvoiceMeNow</th>
                    <th className="px-6 py-4 text-center">Traditional Software</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    ['Monthly Cost', '$0 Forever', '$15-$50/month'],
                    ['Setup Time', '0 seconds', '30-60 minutes'],
                    ['Account Required', 'No (Optional)', 'Yes (Mandatory)'],
                    ['Learning Curve', 'None', 'Moderate to High'],
                    ['Invoice Creation Speed', '< 1 minute', '5-10 minutes'],
                    ['PDF Export', '✓ Included', '✓ Included'],
                    ['Email Delivery', '✓ Included', '✓ Premium Only'],
                    ['Multi-Currency', '✓ 5 Currencies', '✓ Limited'],
                    ['Mobile Access', '✓ Fully Responsive', '✓ App Required'],
                    ['Payment Processing', '✓ Stripe Built-in', '✗ Extra Fee']
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-blue-50 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-900">{row[0]}</td>
                      <td className="px-6 py-4 text-center text-green-600 font-bold">{row[1]}</td>
                      <td className="px-6 py-4 text-center text-gray-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: 'Is InvoiceMeNow really free?',
                a: 'Yes! 100% free forever. No hidden fees, no premium tiers, no trial periods. Create unlimited invoices at no cost.'
              },
              {
                q: 'Do I need to create an account?',
                a: 'No account required to create and send invoices. Optional account creation available if you want to save invoice history.'
              },
              {
                q: 'Can I accept payments through the invoices?',
                a: 'Yes! Built-in Stripe integration allows clients to pay directly from the invoice. You keep all payment processing standard fees apply.'
              },
              {
                q: 'Is my data secure?',
                a: 'Absolutely. All data is encrypted, and we never share your information. Your invoices are private and secure.'
              },
              {
                q: 'Can I use this for my business?',
                a: 'Yes! Our invoices are tax-compliant and professional. Perfect for freelancers, contractors, and small businesses.'
              },
              {
                q: 'How is this different from QuickBooks or FreshBooks?',
                a: 'We focus solely on fast, simple invoicing. No bloated features, no monthly fees, no complex setup. Just billing made easy.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-600">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">Ready to Bill Online Instantly?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Stop paying for complex billing software. Start creating professional invoices right now—completely free.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-blue-50 transition-colors shadow-lg"
          >
            Create Your First Invoice Now
          </Link>
          <p className="mt-4 text-sm text-blue-100">No signup required • Generate invoice in 60 seconds</p>
        </div>
      </div>
    </div>
  )
}

export default OnlineBillingSoftwarePage
